import { Button, Card, CardBody, Combobox, Icon, Paragraph, Switch, TextArea } from "@aidkitorg/component-library";
import { Survey, TargetFieldRef } from "@aidkitorg/types/lib/survey";
import { Dependency, DependencyMap, TRANSLATORS } from "@aidkitorg/types/lib/translation/questions";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import CytoscapeComponent from "react-cytoscapejs";
import { useDebouncedCallback } from "./Hooks/Debounce";
import { InfoDict } from "./Questions/Props";
import { stringToColor } from "./Realtime";

const EDGE_COLOR_MAP: Record<Dependency['type'], string> = {
  formula: '#9ca3f1',
  content: '#FFDD00',
  conditional: 'red',
  // Unused for the moment.
  // notif: 'green',
  // payment: 'magenta',
}

/**
 * Takes in a DependencyMap, a initial TargetField, and a dependency direction and assembles an object with an array
 * of Cytoscape nodes and an array of Cytoscape edges.
 * If one wants to construct a graph of all the fields startId depends on, they would use 'ancestors', and if they
 * wanted a graph of all the fields which depend on startId, they would use 'descendants'
 * 
 * @param map - a map from TargetField to DependencyNode, essentially another TargetField and some additional data. See: DependencyMap in questions.ts
 * @param startId - TargetField from which to begin the traversal
 * @param direction - 'ancestors' to find fields that startId depends on, 'descendants' to find fields that depend on startId
 * @returns nodes: N[] and edges: E[], where N and E are Cytoscape nodes and edges. See: https://js.cytoscape.org/#notation/elements-json
 */
function getAllRelationsByDirection(
  map: DependencyMap,
  startId: TargetFieldRef,
  direction: 'ancestors' | 'descendants',
) {
  // Nodes/fields we've seen already
  const visited = new Set<TargetFieldRef>();
  // Edges as a string key "source>>destination>>type" to ensure uniqueness
  const edgeStrings = new Set<string>();

  // Stack for depth-first-search
  const stack = [startId];

  while (stack.length > 0) {
    const currentId = stack.pop()!;
    // Skip if we’ve already visited
    if (visited.has(currentId)) {
      continue;
    }
    visited.add(currentId);

    const currentNode = map[currentId];
    if (!currentNode) {
      continue;
    }

    // This is the key bit that determines if we're looking up or down the dependency graph
    const relations = direction === 'ancestors' ? currentNode.parents : currentNode.children;

    // Add all not-yet-seen edges to edgeStrings, and not-yet-visited nodes/fields to the stack
    for (const relation of relations) {
      const edgeKey = direction === 'ancestors'
        ? `${relation.field}>>${currentId}>>${relation.type}`
        : `${currentId}>>${relation.field}>>${relation.type}`;

      if (!edgeStrings.has(edgeKey)) {
        edgeStrings.add(edgeKey);
      }

      // Continue traversal if we haven’t seen the relation yet
      if (!visited.has(relation.field)) {
        stack.push(relation.field);
      }
    }
  }

  return {
    nodes: Array.from(visited).map((node) => ({
      data: {
        id: node,
        kind: map[node].component?.kind,
        color: node === startId
          ? '#cefad0' // Unique color assigned to highlight the field being focused on.
          : map[node].component?.kind
            ? stringToColor(map[node].component!.kind)
            : direction === 'ancestors'
              ? '#f2f4fc' // --ak-color-brand-blue-50
              : '#fcfccc', // light yellow
      }
    })),
    edges: Array.from(edgeStrings).map((key) => {
      const [source, target, type] = key.split('>>');
      return {
        data: {
          source,
          target,
          color: EDGE_COLOR_MAP[type as Dependency['type']]
        }
      }
    }),
  };
}

export function TargetFieldExplorer(props: {
  survey: Survey,
  mockInfo: InfoDict,
  setMockInfo: (value: React.SetStateAction<InfoDict>) => void
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showLegend, setShowLegend] = useState(false);
  const cyRef = useRef<cytoscape.Core | null>(null);

  // This is pretty fast, ~ 15-50ms, but no need to recompute it
  // unless props.survey changes
  const dependencyMap = useMemo(() => {
    const map: DependencyMap = {};
    console.time('Map deps time');
    (props.survey ?? []).forEach(collectionComponent => {
      TRANSLATORS[collectionComponent.kind].calculateDependencies((collectionComponent as any), map);
    });
    console.timeEnd('Map deps time');
    return map;
  }, [props.survey]);

  const sortedFields = useMemo(
    () => Object.keys(dependencyMap).sort(),
    [dependencyMap]
  );

  const filteredFields = searchTerm
    ? sortedFields.filter(field => field.toLowerCase().includes(searchTerm.toLowerCase()))
    : sortedFields;

  const graphInfo = useMemo(() => {
    if (!searchTerm || !sortedFields.includes(searchTerm)) return [];

    const ancestors   = getAllRelationsByDirection(dependencyMap, searchTerm, 'ancestors');
    const descendants = getAllRelationsByDirection(dependencyMap, searchTerm, 'descendants');

    return [
      ...ancestors.nodes,
      ...descendants.nodes,
      ...ancestors.edges,
      ...descendants.edges,
    ];
  }, [dependencyMap, searchTerm, sortedFields]);

  useLayoutEffect(() => {
    if (!graphInfo) return;

    // Refresh cytoscape component AFTER we calculate the graph and all DOM mutations
    if (cyRef.current) {
      cyRef.current.resize();
    }
  }, [graphInfo]);

  const handleNewFieldSelection = useDebouncedCallback(
    (field: string) => setSearchTerm(field),
    300,
    [] // Only once
  );

  const onNodeClick = useCallback(
    (evt: cytoscape.EventObject) => handleNewFieldSelection(evt.target.id()),
    [handleNewFieldSelection] // stable because handleNewFieldSelection is stable
  );

  const setCyRef = useCallback((cy: cytoscape.Core) => {
    cyRef.current = cy;
  }, []);

  useEffect(() => {
    const cy = cyRef.current; // Makes TS happy
    if (!cy) return; // still mounting

    cy.nodes().on('click', onNodeClick);
  
    return () => {
      cy.nodes().off('click')
    };  // tidy‑up if component unmounts
  }, [onNodeClick]);

  return <div className='mx-3 h-full'>
    <div className='max-h-1/2 overflow-scroll'>
      <div className='flex items-end'>
        {/* TODO: This should be a Combobox, but currently that component can't handle potentially thousands of fields */}
        <TextArea
          className='w-1/2 mr-4'
          onChange={(event) => handleNewFieldSelection(event.target.value)}
          label={'Search for a Field'}
          showLabel={true}
          rows={1} />
        <Button onClick={() => { props.setMockInfo({}) }} color="blue">Clear Field Values</Button>
      </div>

      {/* Field name and value table */}
      {filteredFields.length > 0
        ? <table>
          <thead>
            <tr>
              <th>Field Name</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            {filteredFields.map((k) =>
              <tr key={k}>
                <td className='border border-gray-300 px-2 max-w-xs overflow-hidden'>{k}</td>
                <td className='border border-gray-300 px-2 max-w-xs wrap-anywhere'>{props.mockInfo[k] ? props.mockInfo[k] : ''}</td>
              </tr>
            )}
          </tbody>
        </table>
        : <p>No fields entered yet</p>
      }
    </div>

    {/* Graph component and toggle-able legend */}
    <div className={`${!!searchTerm ? 'visible' : 'invisible'} relative w-full h-2/3`}>
      <div className='absolute t-1 r-1 z-10 p-2'>
        <Switch
          label="Show Legend"
          onCheckedChange={() => setShowLegend(prev => !prev)}
          className='my-2'
        />
        <div hidden={!showLegend}>
          <Card className='p-4 shadow-md'>
            <CardBody>
              <label>Dependency Type</label>
              {(Object.keys(EDGE_COLOR_MAP) as (keyof typeof EDGE_COLOR_MAP)[]).map((edgeType) => {
                return (
                  <div key={edgeType}>
                    <span className='w-3 h-3 mr-2 inline-block' style={{ background: EDGE_COLOR_MAP[edgeType] }}></span>
                    <span>{edgeType}</span>
                  </div>
                )
              })}
            </CardBody>
          </Card>
        </div>
      </div>

      {/* The Dependency Graph */}
      <CytoscapeComponent
        cy={setCyRef}
        layout={{
          name: 'elk',
          fit: true,
          padding: 0,
          elk: {
            algorithm: 'layered',
            'elk.direction': 'DOWN',
            'elk.layered.spacing.edgeEdgeBetweenLayers': 30,
            'elk.layered.spacing.edgeNodeBetweenLayers': 20,
            'elk.spacing.nodeNode': 20,
            'elk.partitioning.activate': true,
          },
        } as any}
        stylesheet={[
          {
            selector: 'node',
            style: {
              shape: 'round-rectangle',
              width: (node: any) => Math.max(node.data('kind')?.length ?? 0, node.data('id').length) * 9,
              height: 50,
              'background-opacity': '0.8',
              'background-color': 'data(color)',
              label: (node: any) => (node.data('kind') ? node.data('kind') + '\n' : '') + node.data('id'),
              'text-wrap': 'wrap',  // (for multiline)
              'line-height': 1.5,  // you van adjust from here
              'text-valign': 'center',
              'border-width': 1,
              'border-style': 'solid',
              'border-color': '#a9b7e7',
            }
          },
          {
            selector: 'edge',
            style: {
              'line-color': 'data(color)',
              'mid-target-arrow-color': 'data(color)',
              'mid-target-arrow-shape': 'triangle',
              'target-distance-from-node': 3,
              'arrow-scale': 2,
              'curve-style': 'bezier',
            }
          }
        ]}
        elements={graphInfo}
        style={{ width: '100%', height: '100%' }}
      />
    </div>
  </div>
}